// src/components/chrome/TopNav.jsx
// Chrome « E / Clarté » — barre de navigation HORIZONTALE en haut, pleine largeur.
// Réf. visuelle EXACTE : design-mockups/concepts/E-clarte/index.html (.topbar /
// .nav / .icon-btn / .avatar). Blanc translucide + blur, filets quasi invisibles,
// bordeaux au scalpel (seulement sur l'état actif + le point de notification).
//
// Remplace la sidebar encre (Sidebar.jsx) dans le rendu de DashboardV5.
// PRÉSERVATION STRICTE (rien ne doit casser) :
//   - routing : page + handleNav (liens principaux + menu secondaire),
//   - badge emails non-lus (point bordeaux sur l'icône cloche + compteur menu),
//   - toggle clair/sombre (handler existant — E est light-first, le toggle reste),
//   - bouton « Nouvelle conversation » (handler existant),
//   - menu compte/avatar : quota + Paramètres + déconnexion (déplacés du pied
//     de sidebar encre),
//   - mobile : hamburger → menu vertical propre (même handleNav).
// Composant sans état métier : toute la logique vient des props.

import { useState, useRef, useEffect } from "react";
import { colors as C, fonts as F } from "../../theme";
import IAvocatLogo from "../IAvocatLogo";

/* ── icônes filaires 1.7px, cohérentes avec E ── */
const IcBell = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const IcSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);
const IcPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const IcSun = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1v2M8 13v2M15 8h-2M3 8H1M12.95 3.05l-1.41 1.41M4.46 11.54l-1.41 1.41M12.95 12.95l-1.41-1.41M4.46 4.46L3.05 3.05" />
  </svg>
);
const IcMoon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
    <path d="M13 9.5A6 6 0 1 1 6.5 3a5 5 0 0 0 6.5 6.5z" />
  </svg>
);
const IcMenu = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);
const IcClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const IcLogout = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 17l5-5-5-5M15 12H3M21 3v18" />
  </svg>
);
const IcGear = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

export default function TopNav({
  page,
  primaryNav,       // [{ id, label, count }] — liens principaux dans la barre
  secondaryNav,     // [{ id, label }] — entrées du menu compte (ex. Paramètres)
  handleNav,
  onNewConversation,
  onOpenSearch,
  darkMode,
  onToggleDark,
  unreadEmailCount,
  lawyer,
  initials,
  planLabel,
  planLimit,
  planPct,
  monthLabel,
  monthlyUsage,
  onSignOut,
  isCompact,
}) {
  const [menuOpen, setMenuOpen] = useState(false);   // menu compte (desktop + mobile)
  const [navOpen, setNavOpen] = useState(false);     // panneau nav mobile
  const menuRef = useRef(null);

  // Ferme le menu compte au clic extérieur / Échap.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  const go = (id) => { handleNav(id); setNavOpen(false); setMenuOpen(false); };

  // ── point de notification bordeaux (E .icon-btn .dot) ──
  const hasNotif = unreadEmailCount > 0;

  return (
    <header
      style={{
        position: 'sticky', top: 0, zIndex: 50,
        // Fond translucide DÉPENDANT DU THÈME (blanc en clair, encre en sombre) —
        // sinon le texte clair du mode sombre devient illisible sur une barre blanche.
        background: 'color-mix(in srgb, var(--bg-page) 82%, transparent)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <div style={{
        maxWidth: 1180, margin: '0 auto',
        padding: isCompact ? '0 20px' : '0 48px',
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
      }}>
        {/* ── Brand (E .brand) — clic = accueil ── */}
        <button
          onClick={() => go('home')}
          title="Accueil"
          style={{ display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}
        >
          <IAvocatLogo size={30} fontSize={16} gap={11} letterSpacing="-0.02em" />
        </button>

        {/* ── Nav principale (desktop) — E .nav ── */}
        {!isCompact && (
          <nav aria-label="Navigation principale" style={{ display: 'flex', alignItems: 'center', gap: 34 }}>
            {primaryNav.map((n) => {
              const active = page === n.id;
              return (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    position: 'relative',
                    background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
                    fontFamily: FONT, fontSize: 14,
                    fontWeight: active ? 600 : 450,
                    color: active ? C.textPrimary : C.textSecondary,
                    transition: 'color .15s',
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = C.textPrimary; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = C.textSecondary; }}
                >
                  {n.label}
                  {n.count != null && n.count > 0 && (
                    <span style={{
                      fontFamily: FONT, fontSize: 11, fontWeight: 600,
                      lineHeight: 1, padding: '2px 6px', borderRadius: 100,
                      background: active ? C.accent : C.bgAccent,
                      color: active ? '#fff' : C.accent,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {n.count}
                    </span>
                  )}
                  {/* liseré bordeaux discret sous le lien actif */}
                  {active && (
                    <span aria-hidden="true" style={{
                      position: 'absolute', left: 0, right: 0, bottom: -21, height: 2,
                      background: C.accent, borderRadius: 2,
                    }} />
                  )}
                </button>
              );
            })}
          </nav>
        )}

        {/* ── Côté droit ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isCompact ? 10 : 14, flexShrink: 0 }}>
          {/* Recherche (icône desktop) */}
          {!isCompact && (
            <button
              onClick={onOpenSearch}
              aria-label="Rechercher (⌘K)"
              title="Rechercher — ⌘K"
              style={iconBtn()}
              onMouseEnter={hoverIconOn} onMouseLeave={hoverIconOff}
            >
              <IcSearch />
            </button>
          )}

          {/* Toggle clair/sombre (handler existant — toujours câblé) */}
          <button
            onClick={onToggleDark}
            aria-label={darkMode ? 'Passer en mode clair' : 'Passer en mode sombre'}
            title={darkMode ? 'Mode clair' : 'Mode sombre'}
            style={iconBtn()}
            onMouseEnter={hoverIconOn} onMouseLeave={hoverIconOff}
          >
            {darkMode ? <IcSun /> : <IcMoon />}
          </button>

          {/* Notifications — point bordeaux si emails non lus (E .icon-btn .dot) */}
          <button
            onClick={() => go('emails')}
            aria-label={hasNotif ? `${unreadEmailCount} emails non lus` : 'Notifications'}
            title={hasNotif ? `${unreadEmailCount} emails non lus` : 'Notifications'}
            style={{ ...iconBtn(), position: 'relative' }}
            onMouseEnter={hoverIconOn} onMouseLeave={hoverIconOff}
          >
            <IcBell />
            {hasNotif && (
              <span aria-hidden="true" style={{
                position: 'absolute', top: 6, right: 7, width: 7, height: 7,
                borderRadius: '50%', background: C.accent, border: `1.5px solid ${C.bgPage}`,
              }} />
            )}
          </button>

          {/* Nouvelle conversation — seule action « pleine » bordeaux (au scalpel) */}
          {!isCompact && (
            <button
              onClick={onNewConversation}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: C.accent, color: '#fff', border: 'none',
                padding: '9px 15px', borderRadius: 12,
                fontFamily: FONT, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                whiteSpace: 'nowrap', flexShrink: 0,
                boxShadow: C.shadowAccent,
                transition: 'transform var(--dur-instant, 90ms) var(--ease-out, ease)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'none')}
            >
              <IcPlus />
              Nouvelle conversation
            </button>
          )}

          {/* Avatar + menu compte (quota / Paramètres / déconnexion) */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Menu du compte"
              title={`Me ${lawyer?.last_name || lawyer?.first_name || 'Avocat'}`}
              style={{
                width: 34, height: 34, borderRadius: '50%',
                background: C.bgPanel, border: `1px solid ${C.border}`,
                color: C.textPrimary, display: 'grid', placeItems: 'center',
                fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'border-color .15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.borderStrong)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
            >
              {initials}
            </button>

            {menuOpen && (
              <AccountMenu
                lawyer={lawyer}
                planLabel={planLabel}
                planLimit={planLimit}
                planPct={planPct}
                monthLabel={monthLabel}
                monthlyUsage={monthlyUsage}
                secondaryNav={secondaryNav}
                go={go}
                onSignOut={onSignOut}
              />
            )}
          </div>

          {/* Hamburger (mobile) */}
          {isCompact && (
            <button
              onClick={() => setNavOpen(true)}
              aria-label="Ouvrir le menu"
              style={iconBtn()}
              onMouseEnter={hoverIconOn} onMouseLeave={hoverIconOff}
            >
              <IcMenu />
            </button>
          )}
        </div>
      </div>

      {/* ── Panneau de navigation mobile ── */}
      {isCompact && navOpen && (
        <>
          <div
            onClick={() => setNavOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(16,16,15,.32)', zIndex: 60 }}
          />
          <nav
            aria-label="Navigation"
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, width: '82%', maxWidth: 320,
              background: C.bgPage, borderLeft: `1px solid ${C.border}`, zIndex: 70,
              display: 'flex', flexDirection: 'column', padding: '18px 18px 24px',
              animation: 'panelSlide .2s var(--ease-out, ease)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <IAvocatLogo size={26} fontSize={15} gap={10} />
              <button onClick={() => setNavOpen(false)} aria-label="Fermer" style={iconBtn()} onMouseEnter={hoverIconOn} onMouseLeave={hoverIconOff}>
                <IcClose />
              </button>
            </div>

            <button
              onClick={() => { onNewConversation(); setNavOpen(false); }}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: C.accent, color: '#fff', border: 'none',
                padding: '12px 16px', borderRadius: 12, marginBottom: 16,
                fontFamily: FONT, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <IcPlus /> Nouvelle conversation
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[...primaryNav, ...secondaryNav].map((n) => {
                const active = page === n.id;
                return (
                  <button
                    key={n.id}
                    onClick={() => go(n.id)}
                    aria-current={active ? 'page' : undefined}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', padding: '12px 14px', borderRadius: 12, border: 'none',
                      background: active ? C.bgAccent : 'transparent',
                      color: active ? C.accent : C.textPrimary,
                      fontFamily: FONT, fontSize: 15, fontWeight: active ? 600 : 500,
                      textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    {n.label}
                    {n.count != null && n.count > 0 && (
                      <span style={{
                        fontFamily: FONT, fontSize: 12, fontWeight: 600,
                        padding: '2px 8px', borderRadius: 100,
                        background: active ? C.accent : C.bgPanel,
                        color: active ? '#fff' : C.textMuted,
                      }}>{n.count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => { onSignOut(); }}
              style={{
                marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 9,
                padding: '12px 14px', borderRadius: 12,
                border: `1px solid ${C.border}`, background: 'transparent',
                color: C.textSecondary, fontFamily: FONT, fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}
            >
              <IcLogout /> Déconnexion
            </button>
          </nav>
        </>
      )}
    </header>
  );
}

/* ── Menu compte déroulant (desktop + mobile) ── */
function AccountMenu({ lawyer, planLabel, planLimit, planPct, monthLabel, monthlyUsage, secondaryNav, go, onSignOut }) {
  return (
    <div
      role="menu"
      style={{
        position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 268,
        background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18,
        boxShadow: C.shadowMd, padding: 8, zIndex: 80,
        animation: 'fadeIn .14s var(--ease-out, ease)',
      }}
    >
      {/* Identité */}
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
          M<sup>e</sup> {lawyer?.last_name || lawyer?.first_name || 'Avocat'}
        </div>
        <div style={{ fontFamily: FONT, fontSize: 12.5, color: C.textMuted, marginTop: 2 }}>
          {planLabel}{lawyer?.barreau ? ` · ${lawyer.barreau}` : ''}
        </div>
      </div>

      {/* Quota — sobre, filet E (pas d'aplat encre) */}
      {planLimit > 0 && (
        <div style={{
          margin: '0 4px 8px', padding: '11px 12px',
          background: C.bgPanel, border: `1px solid ${C.border}`, borderRadius: 14,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={{ fontFamily: FONT, fontSize: 11.5, color: C.textMuted }}>Consommation · {monthLabel}</span>
            <span style={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: C.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
              {monthlyUsage || 0} <span style={{ color: C.textMuted, fontWeight: 400 }}>/ {planLimit}</span>
            </span>
          </div>
          <div style={{ height: 5, background: C.border, borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ width: `${planPct}%`, height: '100%', background: C.accent, borderRadius: 99 }} />
          </div>
        </div>
      )}

      <div style={{ height: 1, background: C.border, margin: '4px 8px 6px' }} />

      {/* Entrées secondaires (ex. Paramètres) */}
      {secondaryNav.map((n) => (
        <button
          key={n.id}
          role="menuitem"
          onClick={() => go(n.id)}
          style={menuItem()}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.bgHover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <IcGear /> {n.label}
        </button>
      ))}

      {/* Déconnexion */}
      <button
        role="menuitem"
        onClick={() => onSignOut()}
        style={{ ...menuItem(), color: C.accent }}
        onMouseEnter={(e) => (e.currentTarget.style.background = C.bgAccent)}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <IcLogout /> Déconnexion
      </button>
    </div>
  );
}

/* ── styles partagés ── */
function iconBtn() {
  return {
    width: 34, height: 34, borderRadius: '50%',
    border: `1px solid ${C.border}`, background: C.bgCard,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    color: C.textSecondary, cursor: 'pointer', flexShrink: 0,
    transition: 'border-color .15s, color .15s',
  };
}
function hoverIconOn(e) { e.currentTarget.style.borderColor = C.borderStrong; e.currentTarget.style.color = C.textPrimary; }
function hoverIconOff(e) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textSecondary; }
function menuItem() {
  return {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '10px 12px', borderRadius: 12, border: 'none', background: 'transparent',
    color: C.textPrimary, fontFamily: FONT, fontSize: 13.5, fontWeight: 500,
    textAlign: 'left', cursor: 'pointer', transition: 'background .12s',
  };
}
